import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { NCalendars } from '@app/components/Calendars/types';
import {
  formatDailyTasks,
  formatMonthlyTasks,
} from '@app/components/Calendars/utils';
import { SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NTaskListProvider } from '@app/providers/TaskListProvider/types';
import {
  useGetCrmTasksForPeriodMutation,
  useUpdateCrmTaskMutation,
} from '@app/services/rtkQuery/CrmTasks/api';
import type { NCrmTasks } from '@app/services/rtkQuery/CrmTasks/types';
import { generateMonthWeeks } from '@app/utils/generateMonthWeeks';

import type { BaseOptionType } from 'antd/es/select';

export const useTasksCalendar = ({
  selectedEmployee,
  fetchDataForChatHandler,
}: {
  selectedEmployee: BaseOptionType[];
  fetchDataForChatHandler(): void;
}) => {
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const activeTab = useMemo(
    () => searchParams.get('tab') as NTaskListProvider.Tab,
    [searchParams]
  );
  const [hoveredMonthTaskId, setHoveredMonthTaskId] = useState<string | null>(
    null
  );

  const [movedMonthTaskInfo, setMovedMonthTaskInfo] =
    useState<NTaskListProvider.MovedTaskInfo | null>(null);
  const [updatedMovedTask, setUpdatedMovedTask] =
    useState<NCalendars.TaskInterface | null>(null);
  const [taskListForCalendars, setTaskListForCalendars] = useState<
    Array<NCrmTasks.ModelCrmTask>
  >([]);
  const [requestDataForCalendars, setRequestDataForCalendars] =
    useState<NCrmTasks.GetTasksForPeriodRequest | null>(null);
  const [formattedTasks, setFormattedTasks] =
    useState<NCalendars.FormattedTasks>({
      dailyTasks: {
        fullDayTasks: [],
        dailyTaskList: [],
      },
      weeklyTasks: [],
      monthlyTasks: {},
    });
  const currentDate: NTaskListProvider.CurrentDate = useMemo(() => {
    const year = +(searchParams.get('year') || '');
    const month = +(searchParams.get('month') || '');
    const day = +(searchParams.get('day') || '');
    const week = +(searchParams.get('week') || '');

    return { year, month, day, week };
  }, [searchParams]);

  const monthWeeks = useMemo(
    () =>
      generateMonthWeeks({
        year: currentDate.year,
        month: currentDate.month,
      }),
    [currentDate]
  );

  const [
    getTasksForCalendars,
    {
      isLoading: isGetTasksForCalendarsLoading,
      isUninitialized: isGetTasksForCalendarsUninitialized,
    },
  ] = useGetCrmTasksForPeriodMutation();
  const [updateTask] = useUpdateCrmTaskMutation();

  const isCalendarsTabsLoading = useMemo(
    () => isGetTasksForCalendarsLoading || isGetTasksForCalendarsUninitialized,
    [isGetTasksForCalendarsLoading, isGetTasksForCalendarsUninitialized]
  );

  const onChangeHoveredMonthTaskId = useCallback((id: string | null) => {
    setHoveredMonthTaskId(id);
  }, []);

  const onChangeMovedMonthTaskInfo = useCallback(
    (task: NTaskListProvider.MovedTaskInfo) => {
      setMovedMonthTaskInfo(task);
    },
    []
  );

  const getDayTimeRange = useCallback((): [string, string] | null => {
    const { year, month, day } = currentDate;
    const date = moment(`${year}-${month}-${day}`, 'YYYY-M-D');
    if (date.isValid()) {
      const formattedDate = date.format('YYYY-MM-DD');
      const DateS = formattedDate.concat('T00:00:00');
      const DateE = formattedDate.concat('T23:59:59');
      return [DateS, DateE];
    } else {
      return null;
    }
  }, [currentDate]);

  const getWeekTimeRange = useCallback((): [string, string] | null => {
    const { year, week } = currentDate;
    const weekStart = moment(`${year}-${week}`, 'YYYY-W').startOf('isoWeek');
    const weekEnd = moment(`${year}-${week}`, 'YYYY-W').endOf('isoWeek');

    if (weekStart.isValid() && weekEnd.isValid()) {
      const weekStartYear = weekStart.year();
      const weekStartMonth = weekStart.month() + 1;
      const weekStartDay = weekStart.date();
      const weekEndYear = weekEnd.year();
      const weekEndMonth = weekEnd.month() + 1;
      const weekEndDay = weekEnd.date();
      const DateS = moment(
        `${weekStartYear}-${weekStartMonth}-${weekStartDay}`,
        'YYYY-M-D'
      )
        .format('YYYY-MM-DD')
        .concat('T00:00:00');
      const DateE = moment(
        `${weekEndYear}-${weekEndMonth}-${weekEndDay}`,
        'YYYY-M-D'
      )
        .format('YYYY-MM-DD')
        .concat('T00:00:00');
      return [DateS, DateE];
    } else {
      return null;
    }
  }, [currentDate]);

  const getMonthTimeRange = useCallback((): [string, string] | null => {
    const { year, month } = currentDate;
    const monthDate = moment(`${year}-${month}`, 'YYYY-MM');
    if (monthDate.isValid()) {
      const firstVisibleDay = monthWeeks[0].days[0].id;
      const lastVisibleDay = monthWeeks[monthWeeks.length - 1].days[6].id;
      const DateS = moment(firstVisibleDay, 'YYYY-M-D').format(
        'YYYY-MM-DDT00:00:00'
      );
      const DateE = moment(lastVisibleDay, 'YYYY-M-D').format(
        'YYYY-MM-DDT23:59:59'
      );

      return [DateS, DateE];
    } else {
      return null;
    }
  }, [currentDate, monthWeeks]);

  const getTimeRange = useCallback(
    (tab: NTaskListProvider.Tab): [string, string] | null => {
      switch (tab) {
        case 'day':
          return getDayTimeRange();
        case 'week':
          return getWeekTimeRange();
        case 'month':
          return getMonthTimeRange();
        default:
          return null;
      }
    },
    [getDayTimeRange, getWeekTimeRange, getMonthTimeRange]
  );

  const changeFiltersForCalendarTasks = useCallback(() => {
    const range = getTimeRange(activeTab);

    if (!range) return;

    const [DateS, DateE] = range;
    const OwnerIds = selectedEmployee?.map((item) => item.value);

    setRequestDataForCalendars((prev) => ({
      ...prev,
      ActiveBranchId: prev?.ActiveBranchId || ActiveBranchId,
      DateS,
      DateE,
      OwnerIds,
    }));
  }, [getTimeRange, activeTab, ActiveBranchId, selectedEmployee]);

  const fetchTasksForCalendars = useCallback(async () => {
    const tab = activeTab as NTaskListProvider.Tab;
    const isTaskCalendars = tab === 'day' || tab === 'week' || tab === 'month';

    if (!requestDataForCalendars || !isTaskCalendars) return;

    try {
      const { CrmTasks } = await getTasksForCalendars(
        requestDataForCalendars
      ).unwrap();

      setTaskListForCalendars(CrmTasks);
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeTab,
    requestDataForCalendars,
    getTasksForCalendars,
    showNotification,
  ]);

  const formatTasksForCalendars = useCallback(() => {
    const formattedTasks: Array<NCalendars.TaskInterface> =
      taskListForCalendars.map(
        ({ Id, TaskType, TimeStart, TimeDeadline, StatusName }) => ({
          id: Id.toString(),
          title: TaskType || '',
          startTime: TimeStart,
          endTime: TimeDeadline,
          status: StatusName || '',
        })
      );

    switch (activeTab) {
      case 'day':
        const dailyTasks = formatDailyTasks(currentDate, formattedTasks);
        setFormattedTasks((prev) => ({
          ...prev,
          weeklyTasks: [],
          monthlyTasks: {},
          dailyTasks,
        }));
        break;
      case 'month':
        const monthlyTasksMap = formatMonthlyTasks(formattedTasks, {
          dataS: requestDataForCalendars?.DateS || '',
          dateE: requestDataForCalendars?.DateE || '',
        });
        const monthlyTasks = Object.fromEntries(monthlyTasksMap);

        setFormattedTasks((prev) => ({
          ...prev,
          dailyTasks: {
            fullDayTasks: [],
            dailyTaskList: [],
          },
          weeklyTasks: [],
          monthlyTasks,
        }));
        break;
      case 'week':
        const { year, week } = currentDate;
        const weekDays = Array.from({ length: 7 }, (_, i) =>
          moment()
            .year(year)
            .week(week)
            .isoWeekday(i + 1)
        );

        const weeklyTasks = weekDays.map((date) => {
          const year = date.year();
          const month = date.month() + 1;
          const day = date.date();
          const week = date.isoWeek();
          const dailyTasks = formatDailyTasks(
            {
              year,
              month,
              day,
              week,
            },
            formattedTasks
          );
          return dailyTasks;
        });

        setFormattedTasks((prev) => ({
          ...prev,
          dailyTasks: {
            fullDayTasks: [],
            dailyTaskList: [],
          },
          monthlyTasks: {},
          weeklyTasks,
        }));
        break;
    }
  }, [taskListForCalendars, currentDate, activeTab, requestDataForCalendars]);

  const onMouseEnter = useCallback(
    (id: string) => {
      if (movedMonthTaskInfo) {
        const { task, draggabledDay } = movedMonthTaskInfo;
        const newDate = moment(id, 'YYYY-M-D');
        const currentDay = moment(draggabledDay, 'YYYY-MM-DD');

        const dayDifference = newDate.diff(currentDay, 'days');

        const getNewStartTime = () => {
          const newStartTime = moment(task.startTime).add(
            dayDifference,
            'days'
          );
          return newStartTime.format('YYYY-MM-DDTHH:mm:ss');
        };

        const getNewEndTime = () => {
          const taskDuration = moment(task.endTime).diff(
            moment(task.startTime),
            'days'
          );
          const newEndTime = moment(getNewStartTime()).add(
            taskDuration,
            'days'
          );
          return newEndTime.format('YYYY-MM-DDTHH:mm:ss');
        };

        const newStartTime = getNewStartTime();
        const newEndTime = getNewEndTime();
        const formattedTasks: Array<NCalendars.TaskInterface> =
          taskListForCalendars.map(
            ({ Id, TaskType, TimeStart, TimeDeadline, StatusName }) => ({
              id: Id.toString(),
              title: TaskType || '',
              startTime: Id.toString() === task.id ? newStartTime : TimeStart,
              endTime: Id.toString() === task.id ? newEndTime : TimeDeadline,
              status: StatusName || '',
            })
          );

        const updatedTask = formattedTasks.find(({ id }) => id === task.id);
        const monthlyTasksMap = formatMonthlyTasks(formattedTasks, {
          dataS: requestDataForCalendars?.DateS || '',
          dateE: requestDataForCalendars?.DateE || '',
        });
        const monthlyTasks = Object.fromEntries(monthlyTasksMap);
        setUpdatedMovedTask(updatedTask || null);

        setFormattedTasks((prev) => ({
          ...prev,
          monthlyTasks,
        }));
      }
    },
    [movedMonthTaskInfo, taskListForCalendars, requestDataForCalendars]
  );

  const updateTaskRange = useCallback(
    async (id: number, startTime: string, endTime: string) => {
      const task = taskListForCalendars.find(({ Id }) => Id === id);
      if (!task) return;

      try {
        await updateTask({
          ActiveBranchId,
          data: {
            TaskTypeId: task.TaskTypeId,
            TimeStart: startTime,
            TimeDeadline: endTime,
            Id: id,
          },
        }).unwrap();

        fetchDataForChatHandler();

        showNotification('Диапазон задачи успешно сохранен', SUCCESS);

        fetchTasksForCalendars();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      taskListForCalendars,
      updateTask,
      showNotification,
      ActiveBranchId,
      fetchTasksForCalendars,
      fetchDataForChatHandler,
    ]
  );

  useEffect(() => {
    const handleMouseUp = () => {
      if (updatedMovedTask) {
        const { startTime, endTime, id } = updatedMovedTask;
        updateTaskRange(+id, startTime, endTime);
        setMovedMonthTaskInfo(null);
        setUpdatedMovedTask(null);
      }
    };
    if (movedMonthTaskInfo) {
      document.body.style.cursor = 'grabbing';
    } else {
      document.body.style.cursor = 'default';
    }
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [updatedMovedTask, movedMonthTaskInfo, updateTaskRange]);

  useEffect(() => {
    formatTasksForCalendars();
  }, [formatTasksForCalendars]);

  useEffect(() => {
    fetchTasksForCalendars();
  }, [fetchTasksForCalendars]);

  useEffect(() => {
    changeFiltersForCalendarTasks();
  }, [changeFiltersForCalendarTasks]);

  return {
    formattedTasks,
    isCalendarsTabsLoading,
    monthWeeks,
    onMouseEnter,
    hoveredMonthTaskId,
    onChangeHoveredMonthTaskId,
    movedMonthTaskInfo,
    onChangeMovedMonthTaskInfo,
    fetchTasksForCalendars,
    taskListForCalendars,
  };
};
